<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import InlineSvg from 'vue-inline-svg'
import { confettiBurst } from './snowwhite-particles'
import type { Student2Context } from '@/types/student2'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { useNavigation } from '../../useNavigation'

const asset = useStaticAsset()
const { currentUnitId } = useNavigation()
const props = defineProps<{ context: Student2Context }>()

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
const showAnimateBtn = ref(false)
const blinkIntval = ref<ReturnType<typeof setInterval> | null>(null)

const songs = computed(() => props.context.songs)

const getScene = () => {
  return document.querySelector('svg.student-v2-magician__scene') as SVGSVGElement | null
}

const animate = async () => {
  const scene = getScene()
  if (!scene) return

  const cap = scene.querySelector('g#cap')
  const capOnHead = scene.querySelector('g#cap-on-head')
  const lPupil = scene.querySelector('g#left-pupil')
  const rPupil = scene.querySelector('g#right-pupil')
  const mouthInit = scene.querySelector('g#mouth-initial')
  const mouthSmile = scene.querySelector('g#mouth-smile')

  if (!cap || !capOnHead || !lPupil || !rPupil || !mouthInit || !mouthSmile) {
    return
  }

  showAnimateBtn.value = false

  const movecap = cap.animate(
    [
      {},
      { transform: 'translate(80px, -20px) rotate(72deg)', offset: 0.2 },
      { transform: 'translate(160px, 160px) rotate(180deg)', offset: 0.5 },
      { transform: 'translate(248px, 278px) rotate(360deg)', offset: 0.9 },
      { transform: 'translate(238px, 298px) rotate(360deg)' }
    ],
    { delay: 600, duration: 2500, fill: 'forwards', easing: 'ease' }
  )

  await movecap.finished
  cap.animate([{ opacity: 1 }, { opacity: 0 }], { delay: 100, duration: 200, fill: 'forwards' })
  capOnHead.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, fill: 'forwards' })
  ;[lPupil, rPupil].forEach(el => {
    el.animate([{}, { transform: 'translate(1px, 3px)' }], { delay: 100, duration: 200, fill: 'forwards' })
  })

  const mouthAnim = mouthInit.animate([{ opacity: 1 }, { opacity: 0 }], { delay: 100, duration: 200, fill: 'forwards' })

  mouthSmile.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, fill: 'forwards' })

  await mouthAnim.finished
  const anchor = scene.querySelector('#anchor')
  if (anchor) confettiBurst(anchor, 5, { x: 460, y: 550 })

  setTimeout(() => {
    if (blinkIntval.value) {
      clearInterval(blinkIntval.value)
    }
  }, 5000)
}

const startIdleAnimation = async () => {
  const scene = getScene()
  if (!scene) {
    return
  }

  const eyesClosed = scene.querySelector('g#eyes-closed')
  const leftEye = scene.querySelector('g#left-eye')
  const rightEye = scene.querySelector('g#right-eye')

  if (!eyesClosed || !leftEye || !rightEye) {
    return
  }

  blinkIntval.value = setInterval(() => {
    ;[leftEye, rightEye].forEach(el => {
      el.animate(
        [
          { opacity: 1, offset: 0 },
          { opacity: 0, offset: 0 },
          { opacity: 0, offset: 1 },
          { opacity: 1, offset: 1 }
        ],
        { duration: 200 }
      )
    })

    eyesClosed.animate(
      [
        { opacity: 0, offset: 0 },
        { opacity: 1, offset: 0 },
        { opacity: 1, offset: 1 },
        { opacity: 0, offset: 1 }
      ],
      { duration: 200 }
    )
  }, 3000)
}

const animateAcceptedSongs = async () => {
  const scene = getScene()
  if (!scene) return

  await delay(1000)

  for (const i in songs.value) {
    const song = songs.value[i]

    const isLockedUnit = props.context.course_part.units //
      .some(e => e.id === song.unit_id && !e.unlocked)

    const isCurrentUnit = song.unit_id === currentUnitId.value
    const shape = scene.querySelector(`[data-shape-for="${i}"]`)
    const text = scene.querySelector(`[data-text-for="${i}"]`)

    if (!song.accepted) {
      if (isCurrentUnit) text?.setAttribute('fill', '#f05b68')
      else if (isLockedUnit) shape?.setAttribute('fill', '#bababa')
      continue
    }

    shape?.animate([{ fill: 'white' }, { fill: '#f05b68', stroke: 'white' }], { duration: 500, fill: 'forwards' })
    text?.animate([{ fill: '#00BAFF' }, { fill: 'white' }], { duration: 250, fill: 'forwards' })

    await delay(250)
  }

  showAnimateBtn.value = songs.value.every(s => s.accepted === true)
}

const onSceneLoaded = () => {
  startIdleAnimation()
  animateAcceptedSongs()
}

onMounted(() => {
  const checkSvg = async () => {
    const el = getScene()
    if (el) {
      onSceneLoaded()
      return
    }

    await nextTick()
    requestAnimationFrame(() => {
      checkSvg()
    })
  }

  checkSvg()
})

onBeforeUnmount(() => {
  if (blinkIntval.value) {
    clearInterval(blinkIntval.value)
  }
})

defineExpose({ animate, onSceneLoaded, startIdleAnimation, animateAcceptedSongs })
</script>

<template>
  <div class="student-v2-magician">
    <transition name="fade">
      <v-button
        v-if="showAnimateBtn"
        mode="primary filled large"
        class="student-v2-magician__animate-btn"
        @click="animate"
      >
        <v-icon class="mr-1">mdi-party-popper</v-icon>
        Помочь Волшебнику
      </v-button>
    </transition>

    <InlineSvg
      ref="scene"
      class="student-v2-magician__scene"
      :src="asset('img/student-v2/staircase/magician_scene.svg')"
    />
  </div>
</template>

<style lang="scss" scoped>
@function relPxVert($val) {
  @return calc((#{$val} / 780) * 100%);
}

@function relPxHoriz($val) {
  @return calc((#{$val} / 555) * 100%);
}

.student-v2-magician {
  border-radius: 16px;
  border: 1px solid $lightGray;

  aspect-ratio: 555 / 780;
  max-width: min(555px, 90%);
  max-height: calc(100vh - var(--header_height) - 24px);

  position: relative;
  overflow: hidden;
  margin: 0 auto;

  &__animate-btn {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid white;
    border-radius: 8px;
  }
}
</style>
